import {
  Dispatch,
  SetStateAction,
  useState,
} from 'react';
import { ArrowPathIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import { Button } from '../../../../common/Atoms/Button';
import { TextArea } from '../../../../common/Atoms/TextArea';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { AgentTask } from '../../../services/api/aiAgentApi/aiAgent';
import { describeTaskAction } from '../lib/agentDisplay';

import { SendToolReadOnlyDetails } from './SendToolReadOnlyDetails';
import { STATEMENT_DEFAULT_BODY, STATEMENT_DEFAULT_SUBJECT } from './constants';
import {
  getEditableFieldsForTool,
  getInitialTaskEditValues,
  getPendingTaskReadOnlyDetail,
  TaskEditValues,
} from './taskEditHelpers';

export interface PendingTaskApprovalProps {
  task: AgentTask;
  taskEdits?: Record<string, TaskEditValues>;
  setTaskEdits?: Dispatch<SetStateAction<Record<string, TaskEditValues>>>;
  isDecidingTask?: boolean;
  pendingDecisionTaskId?: string | null;
  isProcessing?: boolean;
  onDecideTask: (task: AgentTask, approved: boolean, autoApproveTool?: boolean) => void;
  onSkipAndResolve: (task: AgentTask) => void;
}

export function PendingTaskApproval({
  task,
  taskEdits,
  setTaskEdits,
  isDecidingTask,
  pendingDecisionTaskId,
  isProcessing,
  onDecideTask,
  onSkipAndResolve,
}: PendingTaskApprovalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const editableFields = getEditableFieldsForTool(task.tool_name);
  const isEditableTask = editableFields.length > 0;
  const canEdit = isEditableTask && Boolean(setTaskEdits);
  const taskEdit = (taskEdits && taskEdits[task.id]) || getInitialTaskEditValues(task);
  const isSendTool = [
    `send_email_to_customer`,
    `send_sms_to_customer`,
    `resend_invoice_email`,
    `send_statement`,
  ].includes(task.tool_name);
  const inlineDetail = isSendTool ? undefined : isEditableTask ? getPendingTaskReadOnlyDetail(task) : describeTaskAction(task).detail;
  const isWorking = Boolean(isDecidingTask) && pendingDecisionTaskId === task.id;
  const isBusy = Boolean(isDecidingTask);
  const showSubjectField = editableFields.includes(`subject`);
  const showBodyField = editableFields.includes(`body`);
  const processingLabel = isSendTool ? `Sending...` : `Working...`;
  const inlineEditButton = canEdit &&
    <button
      type={ `button` }
      className={ `ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 transition hover:bg-white hover:text-gray-700` }
      onClick={ () => setIsEditing(prev => !prev) }
    >
      <PencilSquareIcon className={ `h-3.5 w-3.5` } />
      { isEditing ? `Done` : `Edit` }
    </button>;

  return (
    <div className={ `space-y-3` }>
      {
        task.args_edited_by_user_id &&
        <Paragraph
          size={ `xs` }
          variant={ `secondary` }
        >
          { `Edited by staff before sending` }
        </Paragraph>
      }
      {
        inlineDetail &&
        <Paragraph
          size={ `xs` }
          variant={ `secondary` }
          className={ `whitespace-pre-wrap` }
        >
          { inlineDetail }
        </Paragraph>
      }
      {
        canEdit && !isEditing &&
        <div className={ `space-y-2` }>
          <div className={ `flex items-center gap-2` }>
            { inlineEditButton }
          </div>
          <SendToolReadOnlyDetails task={ task } />
        </div>
      }
      {
        canEdit && isEditing && setTaskEdits &&
        <div className={ `space-y-3` }>
          <div className={ `flex items-center gap-2` }>
            { inlineEditButton }
          </div>
          {
            showSubjectField &&
            <TextArea
              label={ `Subject` }
              name={ `task-${task.id}-subject` }
              rows={ 2 }
              value={ taskEdit.subject || `` }
              placeholder={ task.tool_name === `send_statement` ? STATEMENT_DEFAULT_SUBJECT : undefined }
              onChange={ ({ value }) => {
                setTaskEdits(prev => ({
                  ...prev,
                  [task.id]: {
                    ...(prev[task.id] || getInitialTaskEditValues(task)),
                    subject: String(value),
                  },
                }));
              } }
            />
          }
          {
            showBodyField &&
            <TextArea
              label={ `Message` }
              name={ `task-${task.id}-body` }
              rows={ 5 }
              value={ taskEdit.body || `` }
              placeholder={ task.tool_name === `send_statement` ? STATEMENT_DEFAULT_BODY : undefined }
              onChange={ ({ value }) => {
                setTaskEdits(prev => ({
                  ...prev,
                  [task.id]: {
                    ...(prev[task.id] || getInitialTaskEditValues(task)),
                    body: String(value),
                  },
                }));
              } }
            />
          }
        </div>
      }
      {
        isProcessing ?
          <div className={ `flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2` }>
            <ArrowPathIcon className={ `h-4 w-4 animate-spin text-gray-500` } />
            <Paragraph
              size={ `sm` }
              variant={ `secondary` }
            >
              { processingLabel }
            </Paragraph>
          </div> :
          <div className={ `flex flex-wrap gap-2` }>
            <Button
              size={ `sm` }
              loading={ isWorking }
              disabled={ isBusy }
              onClick={ () => onDecideTask(task, true) }
              color={ `green` }
            >
              { `Approve` }
            </Button>
            <Button
              size={ `sm` }
              color={ `orange` }
              disabled={ isBusy }
              onClick={ () => onSkipAndResolve(task) }
            >
              { `Skip & Resolve` }
            </Button>
            <Button
              size={ `sm` }
              color={ `orangeOutline` }
              disabled={ isBusy }
              onClick={ () => onDecideTask(task, false) }
            >
              { `Skip` }
            </Button>
          </div>
      }
    </div>
  );
}
